/**
 * Platform Admin — TanStack Query hooks (AK-216/217/218 Sprint 1).
 *
 * **Список компаний с AK-470 — живой.** Раньше он приходил из
 * `__mocks__/platform-admin.ts` во всех окружениях: `VITE_PLATFORM_ADMIN_USE_MOCK`
 * нигде не пробрасывается в сборку (`Dockerfile` / `docker-compose.yml`), а
 * дефолт был «мок», и обещанного `/api/v1/platform-admin/companies` в бэкенде
 * не существует до сих пор. Админ видел 600 выдуманных тенантов. Войти под
 * пользователем такой компании нельзя — id фикстуры не существует в БД,
 * поэтому список переведён на реальный `GET /api/v1/platform/companies`
 * (AK-4, живёт с самого начала). Мок остался — но теперь только по явному
 * `VITE_PLATFORM_ADMIN_USE_MOCK=true`.
 *
 * Цена перевода: `/platform/companies` не отдаёт тариф, площадь и «платно
 * до» — эти три колонки показывают «—» до отдельной задачи (агрегаты живут
 * в `/companies/{id}/billing` и в tenant-схеме). Пустая ячейка честнее
 * фикстуры.
 *
 * **KPI дашборда с того же захода — живые.** Мок там был по той же причине
 * (флаг не пробрасывается → дефолт «мок»), а «нереализованного эндпоинта» на
 * поверку не оказалось: `GET /api/v1/platform-admin/dashboard/kpis`
 * реализован и подключён (`platform_dashboard.py`, AK-215) — фронт просто
 * стучался мимо, в `/platform-admin/dashboard` без `/kpis`. Теперь ходим по
 * правильному адресу и показываем то, что считает сервер.
 *
 * Чего сервер не считает — того и не рисуем: приростов за 30 дней он не
 * отдаёт, выручка приходит `null` (биллинг не подключён), а в топ-10 нет
 * статуса, числа пользователей и «платно до». Там прочерк и «нет данных»
 * вместо прежних фикстур.
 */
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type UseInfiniteQueryResult,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { InvitationStatus } from '@/pages/platform-admin/companyOnboarding';
import {
  fetchCompaniesListMock,
  type CompaniesListQuery,
  type CompaniesListResponse,
  type TenantStatus,
  type TenantSummary,
} from '@/pages/platform-admin/__mocks__/platform-admin';

const COMPANIES_PAGE = 100;

/** Фильтры без пагинации — для ключа бесконечного списка. */
export interface CompaniesFilter {
  search?: string;
  status?: CompaniesListQuery['status'];
  tariff?: CompaniesListQuery['tariff'];
}

/** Мок — только по явному запросу; по умолчанию ходим в живой бэкенд. */
const USE_MOCK =
  (import.meta.env.VITE_PLATFORM_ADMIN_USE_MOCK as string | undefined) === 'true';

/** `GET /api/v1/platform/companies` (AK-4). */
interface PlatformCompanyRow {
  id: string;
  slug: string;
  name: string;
  access_state: string;
  status: string;
  members_count: number;
}

/**
 * `access_state` компании → статус в колонке «Статус».
 *
 * `grace` и `restricted` — это оттенки просроченной оплаты, отдельной
 * колонки под них нет; показываем как `paused` (доступ придержан), чтобы не
 * плодить значения фильтра ради двух подсостояний биллинга.
 */
export function tenantStatusFromAccessState(
  accessState: string,
  legacyStatus?: string,
): TenantStatus {
  if (legacyStatus === 'archived') return 'archived';
  switch (accessState) {
    case 'trial':
      return 'trial';
    case 'suspended':
      return 'suspended';
    case 'grace':
    case 'restricted':
      return 'paused';
    default:
      return 'active';
  }
}

export function companyRowToTenantSummary(
  row: PlatformCompanyRow,
): TenantSummary {
  return {
    id: row.id,
    name: row.name,
    slug: row.slug,
    status: tenantStatusFromAccessState(row.access_state, row.status),
    // Биллинг и площадь живут в других эндпоинтах — здесь их нет.
    tariff: null,
    users_count: row.members_count ?? 0,
    area_ha: null,
    paid_until: null,
  };
}

/** Конверт серверной пагинации (`envelope=true`, AK-574). */
interface PlatformCompaniesPage {
  items: PlatformCompanyRow[];
  total: number;
  limit: number;
  offset: number;
}

/**
 * Живой список — серверная пагинация (AK-574).
 *
 * Раньше каждая «страница» бесконечного списка тянула ВЕСЬ реестр и резала
 * его на клиенте: пролистал десять порций — десять раз выкачал всё, вместе с
 * агрегатом по участникам на каждого тенанта. Фильтр статуса тоже был
 * клиентским, поэтому `total` врал: он считал строки после фильтра, но до
 * него — только те, что успели приехать.
 *
 * Теперь порция, счётчик и фильтр статуса — на сервере. Шкала статуса одна
 * (`ui_status`, `platform.UI_STATUS_SQL`): та же, по которой дашборд считает
 * «Активных», — чтобы цифра в плитке и длина списка не расходились.
 */
async function fetchCompaniesReal(
  query: CompaniesListQuery,
): Promise<CompaniesListResponse> {
  const { search, status, offset = 0, limit = COMPANIES_PAGE } = query;
  const params = new URLSearchParams();
  if (search?.trim()) params.set('search', search.trim());
  if (status && status !== 'all') params.set('ui_status', status);
  params.set('limit', String(limit));
  params.set('offset', String(offset));
  params.set('envelope', 'true');
  const page = await api.get<PlatformCompaniesPage>(
    `/api/v1/platform/companies?${params.toString()}`,
  );

  // Фильтр по тарифу живой список не поддерживает — тариф не приходит.
  return {
    items: page.items.map(companyRowToTenantSummary),
    total: page.total,
    offset,
    limit,
  };
}

export const platformAdminKeys = {
  dashboard: () => ['platform-admin', 'dashboard'] as const,
  companies: () => ['platform-admin', 'companies'] as const,
  companiesInfinite: (f: CompaniesFilter) =>
    ['platform-admin', 'companies', 'infinite', f] as const,
  invitations: (tenantId: string) =>
    ['platform-admin', 'companies', tenantId, 'invitations'] as const,
};

function fetchCompaniesPage(
  query: CompaniesListQuery,
): Promise<CompaniesListResponse> {
  return USE_MOCK ? fetchCompaniesListMock(query) : fetchCompaniesReal(query);
}

/**
 * AK-348: бесконечный список компаний. Курсор = offset; следующая порция
 * запрашивается, пока накопленных строк меньше `total`.
 */
export function usePlatformCompaniesInfinite(
  filter: CompaniesFilter,
): UseInfiniteQueryResult<
  { items: TenantSummary[]; total: number },
  Error
> {
  return useInfiniteQuery({
    queryKey: platformAdminKeys.companiesInfinite(filter),
    initialPageParam: 0,
    queryFn: ({ pageParam }) =>
      fetchCompaniesPage({
        ...filter,
        offset: pageParam,
        limit: COMPANIES_PAGE,
      }),
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((n, p) => n + p.items.length, 0);
      return loaded < lastPage.total ? loaded : undefined;
    },
    staleTime: 30_000,
    select: (data) => ({
      items: data.pages.flatMap((p) => p.items),
      total: data.pages[0]?.total ?? 0,
    }),
  });
}

/** Строка топ-10 из `platform_dashboard.TenantArea`. */
export interface DashboardTenantArea {
  tenant_id: string;
  slug: string;
  name: string;
  area_ha: number;
}

/**
 * `GET /api/v1/platform-admin/dashboard/kpis` — mirror
 * `platform_dashboard.PlatformDashboardKpis`.
 *
 * `revenue_rub_30d` приходит `null`, пока не подключён биллинг: сервер
 * специально не подставляет 0, чтобы «выручка 0 ₽» не читалась как факт.
 */
export interface DashboardKpis {
  tenants_total: number;
  /** AK-574 — по той же шкале, что бейдж в реестре: не архив + access_state='active'. */
  tenants_active: number;
  tenants_archived: number;
  users_active_30d: number;
  area_ha_total: number;
  /**
   * AK-574 — сколько тенантов не удалось просуммировать (схема не развёрнута).
   * Их гектары в `area_ha_total` не вошли: цифра неполная, и плитка обязана
   * это сказать вслух.
   */
  area_skipped_tenants: number;
  revenue_rub_30d: number | null;
  top_10_by_area: DashboardTenantArea[];
}

export function usePlatformDashboardKpis(): UseQueryResult<DashboardKpis, Error> {
  return useQuery({
    queryKey: platformAdminKeys.dashboard(),
    queryFn: () =>
      api.get<DashboardKpis>('/api/v1/platform-admin/dashboard/kpis'),
    staleTime: 60_000,
  });
}

// ─── Онбординг компании: создание + приглашения (AK-572) ────────────────────

/** Тело `POST /api/v1/platform/companies` — зеркало `platform.CompanyCreateIn`. */
export interface CreateCompanyPayload {
  slug: string;
  name: string;
  email_domain?: string | null;
  /** `null` — администратора не зовём, компания заводится пустой. */
  admin_email: string | null;
  admin_first_name?: string | null;
  admin_last_name?: string | null;
  personal_message?: string | null;
  skip_email?: boolean;
}

/**
 * Ответ создания. `invite_link` сервер кладёт только при `skip_email: true` —
 * когда письмо ушло, ссылка остаётся тайной адресата.
 */
export interface CreateCompanyResult {
  id: string;
  slug: string;
  name: string;
  access_state: string;
  status: string;
  invite_link: string | null;
}

export function useCreateCompany(): UseMutationResult<
  CreateCompanyResult,
  Error,
  CreateCompanyPayload
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateCompanyPayload) =>
      api.post<CreateCompanyResult>('/api/v1/platform/companies', payload),
    onSuccess: () => {
      // Инвалидируем весь префикс компаний: реестр разложен по ключам
      // фильтра, и точечное обновление одной страницы оставило бы соседние
      // вкладки со старым списком.
      void qc.invalidateQueries({ queryKey: platformAdminKeys.companies() });
      void qc.invalidateQueries({ queryKey: platformAdminKeys.dashboard() });
    },
  });
}

/** Строка `GET /api/v1/platform/companies/{id}/invitations`. */
export interface CompanyInvitation {
  id: string;
  email: string;
  /**
   * Ушло ли письмо. `false` — приглашение создано, но почта отказала.
   * Доставка не часть транзакции создания: падать на ней нельзя, иначе
   * человек жмёт «ещё раз» и плодит цепочку отозванных приглашений.
   */
  email_sent?: boolean;
  intended_role: string | null;
  intended_first_name: string | null;
  intended_last_name: string | null;
  invited_by_name: string | null;
  created_at: string | null;
  expires_at: string | null;
  accepted_at: string | null;
  revoked_at: string | null;
  status: InvitationStatus;
}

export function useCompanyInvitations(
  tenantId: string,
  enabled = true,
): UseQueryResult<CompanyInvitation[], Error> {
  return useQuery({
    queryKey: platformAdminKeys.invitations(tenantId),
    queryFn: () =>
      api.get<CompanyInvitation[]>(
        `/api/v1/platform/companies/${tenantId}/invitations`,
      ),
    enabled: enabled && !!tenantId,
    staleTime: 15_000,
  });
}

/** Строка превью массовой загрузки — зеркало `platform.BulkPreviewRow`. */
export interface BulkPreviewRow {
  row: number;
  email: string | null;
  first_name: string | null;
  last_name: string | null;
  role: string | null;
  personal_message: string | null;
  is_valid: boolean;
  errors: string[];
}

export interface BulkPreviewResult {
  scope: string;
  scope_id: string | null;
  scope_label: string | null;
  valid_rows: BulkPreviewRow[];
  invalid_rows: BulkPreviewRow[];
  duplicate_rows: BulkPreviewRow[];
}

export function useBulkInvitePreview(): UseMutationResult<
  BulkPreviewResult,
  Error,
  { tenantId: string; file: File }
> {
  return useMutation({
    mutationFn: ({ tenantId, file }) => {
      const form = new FormData();
      form.append('file', file);
      form.append('scope', 'company');
      return api.uploadFile<BulkPreviewResult>(
        `/api/v1/platform/companies/${tenantId}/invitations/bulk/preview`,
        form,
      );
    },
  });
}

export interface BulkConfirmResult {
  created: number;
  skipped: number;
  errors: string[];
}

/**
 * Подтверждение приглашений. Одиночное приглашение ходит сюда же с одной
 * строкой: отдельной ручки на бэкенде нет, а собирать ради одного человека
 * xlsx на клиенте — лишний слой, который может разойтись с парсером.
 */
export function useBulkInviteConfirm(): UseMutationResult<
  BulkConfirmResult,
  Error,
  { tenantId: string; rows: BulkPreviewRow[] }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ tenantId, rows }) =>
      api.post<BulkConfirmResult>(
        `/api/v1/platform/companies/${tenantId}/invitations/bulk`,
        { scope: 'company', scope_id: null, rows },
      ),
    onSuccess: (_result, { tenantId }) => {
      void qc.invalidateQueries({
        queryKey: platformAdminKeys.invitations(tenantId),
      });
    },
  });
}

/** Адрес xlsx-шаблона — качается обычной ссылкой, cookie-сессия та же. */
export function inviteTemplateUrl(tenantId: string): string {
  return `/api/v1/platform/companies/${tenantId}/invitations/template`;
}

/** Одна строка «как из файла» — для одиночного приглашения. */
export function singleInviteRow(input: {
  email: string;
  firstName?: string;
  lastName?: string;
  role: string;
  personalMessage?: string;
}): BulkPreviewRow {
  return {
    row: 1,
    email: input.email.trim().toLowerCase(),
    first_name: input.firstName?.trim() || null,
    last_name: input.lastName?.trim() || null,
    role: input.role,
    personal_message: input.personalMessage?.trim() || null,
    is_valid: true,
    errors: [],
  };
}

// ─── Архивация компании и переотправка приглашения (AK-572, вторая половина) ─

/**
 * Архивация компании — мягкая: `status='archived'`, данные на месте, сессии
 * отозваны. Подтверждение вводом названия обязательно и проверяется сервером
 * тоже: диалог можно обойти, ручку — нет.
 */
export function useArchiveCompany(): UseMutationResult<
  unknown,
  Error,
  { tenantId: string; confirmName: string; reason?: string }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ tenantId, confirmName, reason }) =>
      api.post(`/api/v1/platform/companies/${tenantId}/archive`, {
        confirm_name: confirmName,
        reason: reason ?? null,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: platformAdminKeys.companies() });
    },
  });
}

export function useRestoreCompany(): UseMutationResult<
  unknown,
  Error,
  { tenantId: string }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ tenantId }) =>
      api.post(`/api/v1/platform/companies/${tenantId}/restore`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: platformAdminKeys.companies() });
    },
  });
}

/**
 * «Отправить ещё раз». Сервер отзывает старое приглашение и заводит свежее:
 * повторить прежнее письмо нельзя — токен хранится хешем, исходной ссылки
 * нет нигде, кроме уже отправленного письма.
 */
export function useResendInvitation(): UseMutationResult<
  CompanyInvitation,
  Error,
  { tenantId: string; invitationId: string }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ tenantId, invitationId }) =>
      api.post<CompanyInvitation>(
        `/api/v1/platform/companies/${tenantId}/invitations/${invitationId}/resend`,
      ),
    onSuccess: (_r, { tenantId }) => {
      void qc.invalidateQueries({
        queryKey: platformAdminKeys.invitations(tenantId),
      });
    },
  });
}

/**
 * Приостановка доступа: `access_state = 'suspended'` (шкала биллинга) — и
 * возврат в `active`.
 *
 * Это НЕ архивация, хотя на глаз похоже. Приостановка живёт на биллинговой
 * оси и снимается пересчётом подписки; архив — на `tenants.status`, из-за
 * которого компания исчезает из доступных пользователю
 * (`get_user_memberships` фильтрует по нему). Два разных выключателя, и
 * подменять один другим нельзя: снятый платёж вернул бы «архивную» компанию
 * к жизни сам.
 */
export function useSetCompanyAccessState(): UseMutationResult<
  unknown,
  Error,
  { tenantId: string; accessState: 'active' | 'suspended'; reason?: string }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ tenantId, accessState, reason }) =>
      api.patch(`/api/v1/platform/companies/${tenantId}/access-state`, {
        access_state: accessState,
        reason: reason ?? null,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: platformAdminKeys.companies() });
    },
  });
}

/**
 * «Войти в компанию» — платформенный админ остаётся собой и смотрит чужой
 * тенант. Режим (полный / только чтение) считает сервер по роли админа.
 */
export function useImpersonateCompany(): UseMutationResult<
  { tenant_id: string; tenant_name: string; expires_at: string },
  Error,
  { tenantId: string }
> {
  return useMutation({
    mutationFn: ({ tenantId }) =>
      api.post<{ tenant_id: string; tenant_name: string; expires_at: string }>(
        `/api/v1/platform/companies/${tenantId}/impersonate`,
      ),
  });
}
